import { ErrorAlert } from '@if/mui'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useFeatureFlags } from '@src/api/app-config-hooks'
import { useMatchInteracWire, useResetInteracPossibleMatch } from '@src/api/interac-wire-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { EInteracWireStatus, InteracPossibleMatch, InteracWire } from '@src/types/interac'
import { format as formatDate, parseISO } from '@if/core/date'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InteracWireInformation from './InteracWireInformation'
import InteracWirePossibleMatch from './InteracWirePossibleMatch'

interface InteractWireMatchDialogProps {
  interacWire: InteracWire | null
  onClose: () => void
}

const InteracWireMatchDialog = ({ interacWire, onClose }: InteractWireMatchDialogProps) => {
  const { t } = useTranslation()
  const { useLoanDateOverrideInteracTransfers } = useFeatureFlags()
  const [dateOverride, setDateOverride] = useState<Date | null>(null)
  const {
    mutateAsync: matchInteracWire,
    isPending: isMatchPending,
    isError: isMatchError,
    reset: resetMatchState,
  } = useMatchInteracWire()
  const {
    mutateAsync: resetInteracPossibleMatch,
    isPending: isResetPending,
    isError: isResetError,
    reset: resetPossibleMatchState,
  } = useResetInteracPossibleMatch()

  const isError = isMatchError || isResetError
  const isPending = isMatchPending || isResetPending

  const { showSuccess } = useNotification()

  const handleClose = useCallback(() => {
    if (isPending) return
    resetMatchState()
    resetPossibleMatchState()
    setDateOverride(null)
    onClose()
  }, [isPending, onClose, resetMatchState, resetPossibleMatchState])

  const handleMatch = useCallback(
    (possibleMatch: InteracPossibleMatch) => {
      if (!interacWire || isPending) return

      void matchInteracWire({
        id: interacWire.id,
        versionTag: interacWire.versionTag,
        loanId: possibleMatch.loanId,
        dateOverride: dateOverride ? formatDate(dateOverride, 'yyyy-MM-dd') : null,
      }).then(() => {
        resetMatchState()
        resetPossibleMatchState()
        setDateOverride(null)
        onClose()
        showSuccess()
      })
    },
    [
      interacWire,
      isPending,
      matchInteracWire,
      dateOverride,
      onClose,
      showSuccess,
      resetMatchState,
      resetPossibleMatchState,
    ],
  )

  const handleResetPossibleMatch = useCallback(() => {
    if (!interacWire || isPending) return

    void resetInteracPossibleMatch(interacWire.id).then(() => {
      onClose()
      showSuccess()
    })
  }, [interacWire, isPending, resetInteracPossibleMatch, onClose, showSuccess])

  const possibleMatches = useMemo(
    () =>
      (interacWire?.possibleMatches || []).map((item) => (
        <InteracWirePossibleMatch
          key={item.loanId}
          possibleMatch={item}
          variant="outlined"
          onSelect={handleMatch}
          disabled={isPending || interacWire?.status == EInteracWireStatus.InProcess}
        />
      )),
    [interacWire?.possibleMatches, interacWire?.status, handleMatch, isPending],
  )

  return (
    <Dialog open={Boolean(interacWire)} onClose={handleClose} maxWidth="lg">
      <DialogTitle>
        <Stack gap={1}>
          <InteracWireInformation interacWire={interacWire} />
          {useLoanDateOverrideInteracTransfers && interacWire && (
            <DatePicker
              label={t('interacWire.dateOverride')}
              value={dateOverride}
              onChange={setDateOverride}
              minDate={parseISO(interacWire.receivedOn)}
              slotProps={{ field: { clearable: true }, textField: { size: 'small' } }}
            />
          )}
        </Stack>

        {isError && <ErrorAlert sx={{ mt: 1 }} disabledGutterBottom />}
      </DialogTitle>

      {isPending && <LinearProgress />}

      <DialogContent dividers>
        <Stack spacing={1} style={{ position: 'relative' }}>
          {possibleMatches}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Tooltip title={t('interacWire.noMatchDescription')}>
          <Button onClick={handleResetPossibleMatch} color="primary" variant="contained" loading={isPending}>
            {t('interacWire.noMatch')}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}

export default InteracWireMatchDialog
